// Gate 5.3 · Facebook OAuth callback landing page.
// Users arrive here after Meta redirects them back through
// /api/brand-os/publish/oauth/facebook/callback. This page:
//   1. Reads the `session` and optional `error` params from the URL.
//   2. Fetches the pending OAuth session to render a Page picker.
//   3. POSTs the finalize action; on success bounces back to the
//      Channel Connections admin with a success flash.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

export default function FacebookOAuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session") || "";
  const errorFromMeta = params.get("error");

  const [session, setSession] = useState(null);
  const [pickedPageId, setPickedPageId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [isDefault, setIsDefault] = useState(true);
  const [loadState, setLoadState] = useState({ loading: true, error: null });
  const [submitState, setSubmitState] = useState({ submitting: false, error: null });

  const bounceBack = useCallback((flash, err) => {
    const search = new URLSearchParams();
    if (flash) search.set("oauth_result", encodeURIComponent(flash));
    if (err) search.set("oauth_error", encodeURIComponent(err));
    navigate({
      pathname: "/brand-os/admin/channel-connections",
      search: `?${search.toString()}`,
    }, { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (errorFromMeta) {
      setLoadState({ loading: false, error: humanizeError(errorFromMeta) });
      return;
    }
    if (!sessionId) {
      setLoadState({ loading: false, error: "Missing session id in URL." });
      return;
    }
    (async () => {
      try {
        const r = await api.get(`/publish/oauth/facebook/session/${sessionId}`);
        setSession(r.data);
        const pages = r.data.pages || [];
        if (pages.length === 1) {
          setPickedPageId(String(pages[0].id));
          setDisplayName(pages[0].name ? `Facebook · ${pages[0].name}` : "");
        }
        setLoadState({ loading: false, error: null });
      } catch (e) {
        setLoadState({ loading: false, error: formatApiError(e) });
      }
    })();
  }, [sessionId, errorFromMeta]);

  useEffect(() => {
    // When the operator changes the Page pick, refresh the default
    // display name to reflect the newly-picked Page (unless they've
    // already customised it).
    const page = (session?.pages || []).find(
      (p) => String(p.id) === pickedPageId,
    );
    if (page && !displayName.trim().startsWith("Facebook ·")) {
      // don't clobber a fully custom name
      return;
    }
    if (page) setDisplayName(`Facebook · ${page.name}`);
  }, [pickedPageId, session?.pages]); // eslint-disable-line react-hooks/exhaustive-deps

  const state = useMemo(() => (session?.state || "").toLowerCase(), [session]);

  async function handleFinalize(e) {
    e.preventDefault();
    if (!pickedPageId) return;
    setSubmitState({ submitting: true, error: null });
    try {
      const r = await api.post("/publish/oauth/facebook/finalize", {
        session_id: sessionId,
        page_id: pickedPageId,
        display_name: displayName.trim() || undefined,
        is_default: isDefault,
      });
      const conn = r.data.connection;
      const verb = r.data.intent === "reconnect" ? "Reconnected" : "Connected";
      bounceBack(
        `${verb} ${conn.display_name} (fp ${conn.secret_fingerprint}).`,
      );
    } catch (e2) {
      setSubmitState({ submitting: false, error: formatApiError(e2) });
    }
  }

  return (
    <Shell title="Connect Facebook · OAuth">
      <div className="bo-page" data-testid="facebook-oauth-callback-page">
        <section className="bo-card">
          <div className="bo-eyebrow">Publish Infrastructure</div>
          <h1 className="bo-serif" style={{ fontSize: 24, margin: "6px 0 8px" }}>
            Finalise your Facebook connection
          </h1>
          <p style={{ color: "var(--bo-muted)", maxWidth: 720, lineHeight: 1.55 }}>
            You're one step away from a managed OAuth connection to a Facebook Page.
            Pick the Page you want Brand OS to publish to. Your Page access token is
            encrypted with Fernet the moment it lands on our server; it is never
            written to logs, audit rows, or Publish Receipts.
          </p>
        </section>

        <section style={{ marginTop: 18 }}>
          <ErrorBanner error={loadState.error} testId="oauth-callback-load-error" />
          {loadState.loading && <LoadingCard label="Loading OAuth session…" />}

          {!loadState.loading && !loadState.error && session && state !== "pending_page_pick" && (
            <div
              className="bo-card"
              data-testid={`oauth-session-state-${state}`}
              style={{ marginTop: 12 }}
            >
              <div className="bo-eyebrow">Session {state.replace(/_/g, " ")}</div>
              <p style={{ marginTop: 6, color: "var(--bo-muted)" }}>
                {state === "finalized"
                  ? "This OAuth session has already been finalised. Head back to Channel Connections."
                  : "This OAuth session has expired. Please start a new Connect Facebook flow."}
              </p>
              <div style={{ marginTop: 14 }}>
                <button
                  className="bo-btn bo-btn-primary"
                  data-testid="oauth-back-to-connections"
                  onClick={() => bounceBack(null, null)}
                >
                  Back to Channel Connections
                </button>
              </div>
            </div>
          )}

          {!loadState.loading && !loadState.error && session
            && state === "pending_page_pick" && (session.pages?.length ?? 0) === 0 && (
              <OAuthDiagnosticPanel
                diagnostic={session.diagnostic || {}}
                onBackToConnections={() => bounceBack(null, null)}
              />
            )}

          {!loadState.loading && !loadState.error && session
            && state === "pending_page_pick" && (session.pages?.length ?? 0) > 0 && (
              <div className="bo-card" style={{ marginTop: 12 }}>
                <div className="bo-eyebrow">
                  Signed in as {session.fb_user_name || "Meta user"}
                </div>
                <h2 className="bo-serif" style={{ fontSize: 18, margin: "6px 0 4px" }}>
                  Choose a Page to bind
                </h2>
                <p style={{ color: "var(--bo-muted)", fontSize: 14 }}>
                  {session.intent === "reconnect"
                    ? "This will refresh the token on the existing connection so schedules keep working."
                    : "This creates a new OAUTH_MANAGED channel connection."}
                </p>

                <form
                  onSubmit={handleFinalize}
                  style={{ marginTop: 18, display: "grid", gap: 14 }}
                  data-testid="oauth-finalize-form"
                >
                  <ErrorBanner error={submitState.error} testId="oauth-finalize-error" />

                  <div
                    role="radiogroup"
                    aria-label="Select Facebook Page"
                    style={{ display: "grid", gap: 10 }}
                  >
                    {(session.pages || []).map((p) => {
                      const checked = String(p.id) === pickedPageId;
                      return (
                        <label
                          key={p.id}
                          data-testid={`oauth-page-option-${p.id}`}
                          style={{
                            display: "flex",
                            gap: 12,
                            alignItems: "flex-start",
                            padding: "12px 14px",
                            borderRadius: 8,
                            border: `1px solid ${checked ? "var(--bo-accent)" : "var(--bo-line)"}`,
                            background: checked ? "var(--bo-accent-soft)" : "var(--bo-panel)",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="radio"
                            name="page_id"
                            value={p.id}
                            checked={checked}
                            onChange={() => setPickedPageId(String(p.id))}
                            data-testid={`oauth-page-radio-${p.id}`}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontFamily: "var(--bo-serif)", fontSize: 16 }}>
                              {p.name || "(unnamed page)"}
                            </div>
                            <div className="bo-mono" style={{ fontSize: 12, color: "var(--bo-muted)" }}>
                              id {p.id} {p.category ? `· ${p.category}` : ""}
                            </div>
                            {(p.tasks || []).length > 0 && (
                              <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 6 }}>
                                {p.tasks.map((t) => (
                                  <Pill key={t} tone="muted" testId={`oauth-page-task-${p.id}-${t}`}>
                                    {t}
                                  </Pill>
                                ))}
                              </div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  <div className="bo-field">
                    <label className="bo-label">Connection display name</label>
                    <input
                      className="bo-input"
                      data-testid="oauth-display-name"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="Facebook · Precipice Health"
                    />
                    <div className="bo-hint">Shown in the Channel Connections list. Safe to change later.</div>
                  </div>

                  <label
                    className="bo-check"
                    data-testid="oauth-set-default"
                    style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
                  >
                    <input
                      type="checkbox"
                      checked={isDefault}
                      onChange={(e) => setIsDefault(e.target.checked)}
                    />
                    <span>Use as the default Facebook connection</span>
                  </label>

                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      className="bo-btn bo-btn-ghost"
                      data-testid="oauth-finalize-cancel"
                      onClick={() => bounceBack(null, null)}
                      disabled={submitState.submitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="bo-btn bo-btn-primary"
                      data-testid="oauth-finalize-submit"
                      disabled={submitState.submitting || !pickedPageId}
                    >
                      {submitState.submitting ? "Finalising…" : "Finalise connection"}
                    </button>
                  </div>
                </form>
              </div>
            )}
        </section>
      </div>
    </Shell>
  );
}

function humanizeError(code) {
  const map = {
    user_denied: "You cancelled the Meta consent screen. No connection was created.",
    access_denied: "Meta reported that access was denied. No connection was created.",
    state_expired: "The connect link expired. Please restart the connect flow.",
    state_invalid_signature: "The connect link is invalid. Please restart the connect flow.",
    state_malformed: "The connect link is malformed. Please restart the connect flow.",
    state_channel_mismatch: "The connect link is bound to a different channel.",
    missing_code_or_state: "Meta did not return the expected callback parameters.",
    code_exchange_failed: "Meta rejected our authorization code exchange.",
    long_lived_exchange_failed: "Meta could not issue a long-lived token.",
    pages_listing_failed: "Meta refused to list the Pages this account manages.",
  };
  return map[code] || `Meta OAuth failed: ${code}`;
}


// ---------------------------------------------------------------------------
// OAuthDiagnosticPanel
//
// Gate 5.3 · Roger 2026-08-18. When /me/accounts returns 0 Pages, we
// used to show a generic "No Pages found" empty state. That silent
// failure was the same class of visibility bug we fixed on Preflight;
// this panel replaces it with a full diagnostic view:
//
//   * The exact identity Meta returned (user id + name)
//   * Every scope Meta granted (and every one it didn't)
//   * A plain-English diagnosis of the most likely root cause
//   * Three ranked remediation cards the operator can walk through
//   * A "Try again" button that restarts the connect flow
// ---------------------------------------------------------------------------
function OAuthDiagnosticPanel({ diagnostic, onBackToConnections }) {
  const {
    meta_user_id,
    meta_user_name,
    granted_scopes = [],
    required_scopes = [],
    missing_scopes = [],
    pages_returned,
    app_id,
    diagnosis,
    remediation = [],
  } = diagnostic || {};
  return (
    <div
      className="bo-card"
      data-testid="oauth-diagnostic-panel"
      style={{
        marginTop: 12,
        borderLeft: "3px solid var(--bo-danger)",
      }}
    >
      <div className="bo-eyebrow">OAuth diagnostic</div>
      <h2 className="bo-serif" style={{ fontSize: 20, margin: "6px 0 8px" }}>
        Meta authorised the connection, but returned no manageable Pages
      </h2>
      {diagnosis && (
        <p
          data-testid="oauth-diagnostic-diagnosis"
          style={{
            padding: "10px 12px",
            borderRadius: 6,
            background: "var(--bo-danger-soft)",
            color: "var(--bo-danger)",
            fontSize: 14,
            lineHeight: 1.55,
          }}
        >
          <strong>Most likely cause: </strong>{diagnosis}
        </p>
      )}

      <div
        style={{
          marginTop: 14,
          padding: "12px 14px",
          borderRadius: 8,
          background: "var(--bo-panel-sunken)",
          fontSize: 13,
          lineHeight: 1.6,
        }}
        data-testid="oauth-diagnostic-facts"
      >
        <div className="bo-eyebrow" style={{ margin: "0 0 6px 0" }}>
          What Meta actually returned
        </div>
        <FactRow label="Meta user" value={meta_user_name
          ? `${meta_user_name} (id ${meta_user_id})`
          : meta_user_id || "(no id)"} testId="oauth-fact-user" />
        <FactRow label="App" value={app_id || "(unknown)"} testId="oauth-fact-app" />
        <FactRow
          label="Pages returned"
          value={<span style={{ color: pages_returned === 0 ? "var(--bo-danger)" : "var(--bo-ink)" }}>
            {pages_returned ?? 0}
          </span>}
          testId="oauth-fact-pages-returned"
        />
        <FactRow
          label="Granted scopes"
          value={granted_scopes.length
            ? <ScopeChips scopes={granted_scopes} required={required_scopes} tone="granted" />
            : <em style={{ color: "var(--bo-muted)" }}>none</em>}
          testId="oauth-fact-granted-scopes"
        />
        {missing_scopes.length > 0 && (
          <FactRow
            label="Missing scopes"
            value={<ScopeChips scopes={missing_scopes} tone="missing" />}
            testId="oauth-fact-missing-scopes"
          />
        )}
      </div>

      {remediation.length > 0 && (
        <div style={{ marginTop: 14 }} data-testid="oauth-diagnostic-remediation">
          <div className="bo-eyebrow" style={{ margin: "0 0 8px 0" }}>
            Walk through these fixes in order
          </div>
          <div style={{ display: "grid", gap: 10 }}>
            {remediation.map((r, i) => (
              <div
                key={i}
                data-testid={`oauth-remediation-${i}`}
                style={{
                  padding: "12px 14px",
                  borderRadius: 8,
                  background: "var(--bo-panel)",
                  border: "1px solid var(--bo-line)",
                }}
              >
                <div style={{ fontFamily: "var(--bo-serif)", fontSize: 15, marginBottom: 4 }}>
                  {r.title}
                </div>
                <div style={{ fontSize: 13, color: "var(--bo-muted)", lineHeight: 1.55, marginBottom: 6 }}>
                  {r.detail}
                </div>
                {r.action && (
                  <div style={{ fontSize: 13, lineHeight: 1.55 }}>
                    <strong style={{ marginRight: 4 }}>Do:</strong>{r.action}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <button
          className="bo-btn bo-btn-ghost"
          data-testid="oauth-diagnostic-back"
          onClick={onBackToConnections}
        >
          Back to Channel Connections
        </button>
        <a
          className="bo-btn bo-btn-primary"
          data-testid="oauth-diagnostic-retry"
          href="/brand-os/admin/channel-connections?oauth_retry=1"
        >
          Retry Connect Facebook →
        </a>
      </div>
    </div>
  );
}

function FactRow({ label, value, testId }) {
  return (
    <div
      data-testid={testId}
      style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "4px 0", borderTop: "1px solid var(--bo-line)" }}
    >
      <div style={{ width: 160, fontSize: 12, color: "var(--bo-muted)", flexShrink: 0 }}>{label}</div>
      <div className="bo-mono" style={{ fontSize: 13, wordBreak: "break-all", flex: 1 }}>{value}</div>
    </div>
  );
}

function ScopeChips({ scopes, required, tone }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {scopes.map((s) => {
        const isRequired = required ? required.includes(s) : false;
        return (
          <span
            key={s}
            data-testid={`scope-chip-${s}`}
            style={{
              padding: "3px 8px",
              borderRadius: 4,
              fontSize: 11,
              fontFamily: "var(--bo-mono)",
              background: tone === "missing" ? "var(--bo-danger-soft)"
                : isRequired ? "var(--bo-success-soft)" : "var(--bo-panel-sunken)",
              color: tone === "missing" ? "var(--bo-danger)"
                : isRequired ? "var(--bo-success)" : "var(--bo-ink-soft)",
            }}
          >
            {s}
          </span>
        );
      })}
    </div>
  );
}
